import { useIntl } from 'react-intl'
import { IPropertyCard } from '@awazevr/design-system.compositions.property-card'
import { useFormatCurrency } from 'src/utils/globalProps'

type PropertyCardTranslations = IPropertyCard['translations']

export const usePropertyCardTranslations = (): ((property: {
  title: string
}) => PropertyCardTranslations) => {
  const { formatDate, formatMessage } = useIntl()
  const { formatCurrencyToObject } = useFormatCurrency()

  return (property) => ({
    priceUnavailableCTALabel: formatMessage({
      id: 'property-carousel.unavailable-price',
      defaultMessage: 'For price, add a start date',
    }),
    calloutBadge: formatMessage({
      id: 'property-carousel.callout-badge',
      defaultMessage: "Customers' Choice",
    }),
    reviewRating: formatMessage({
      id: 'property-carousel.rating',
      defaultMessage: 'Feefo rating',
    }),
    outOf: formatMessage({
      id: 'property-carousel.out-of',
      defaultMessage: 'out of',
    }),
    stars: formatMessage({
      id: 'property-carousel.stars',
      defaultMessage: 'stars',
    }),
    nights: formatMessage({
      id: 'property-carousel.nights',
      defaultMessage: 'nights',
    }),
    from: formatMessage({
      id: 'property-carousel.from',
      defaultMessage: 'From',
    }),
    linkTo: formatMessage({
      id: 'property-carousel.link-to',
      defaultMessage: 'Link to',
    }),
    propertyLinkLabel: formatMessage({
      id: 'property-carousel.view',
      defaultMessage: 'View',
    }),
    dateFormatter: (date: string) =>
      `${formatDate(date, { day: 'numeric' })} ${formatDate(date, {
        month: 'short',
      })}`,
    reserveTodayForOnly: formatMessage({
      id: 'property-carousel.reserve-today',
      defaultMessage: 'Reserve today for only',
    }),
    includedExtrasLabel: formatMessage({
      id: 'property-carousel.includedExtras',
      defaultMessage: 'Price includes',
    }),
    propertyLinkAriaLabel: formatMessage(
      {
        id: 'property-carousel.link-aria-label',
        defaultMessage: 'view {title} (opens in a new window)',
      },
      { title: property.title },
    ),
    formatCurrency: formatCurrencyToObject,
    urgencyBadge: formatMessage({
      id: 'property-carousel.urgency-badge',
      defaultMessage: "Book before it's gone!",
    }),
    reserveForSelectedProperties: formatMessage({
      id: 'property-carousel.reserve-for-selected-properties',
      defaultMessage: 'on selected properties',
    }),
    propertySpecs: {
      guests: {
        singular: formatMessage({
          id: 'property-specs.guests.singular',
          defaultMessage: 'Guest',
        }),
        plural: formatMessage({
          id: 'property-specs.guests.plural',
          defaultMessage: 'Guests',
        }),
      },
      studio: formatMessage({
        id: 'property-specs.studio',
        defaultMessage: 'Studio',
      }),
      bedrooms: {
        singular: formatMessage({
          id: 'property-specs.bedrooms.singular',
          defaultMessage: 'Bedroom',
        }),
        plural: formatMessage({
          id: 'property-specs.bedrooms.plural',
          defaultMessage: 'Bedrooms',
        }),
      },
      bathrooms: {
        singular: formatMessage({
          id: 'property-specs.bathrooms.singular',
          defaultMessage: 'Bathroom',
        }),
        plural: formatMessage({
          id: 'property-specs.bathrooms.plural',
          defaultMessage: 'Bathrooms',
        }),
      },
      pets: {
        label: '',
        singular: formatMessage({
          id: 'property-specs.pets.singular',
          defaultMessage: 'Pet',
        }),
        plural: formatMessage({
          id: 'property-specs.pets.plural',
          defaultMessage: 'Pets',
        }),
        allowed: '',
      },
    },
  })
}
